import { memo, useCallback, useMemo, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import DeleteButton from '../../../../../../components/Filters/DeleteButton';
import Scrollbar from '../../../../../../components/Scrollbar';
import ActiveFilterSection from '../../../../../../components/Filters/ActiveFilterSection';
import SidebarListItem from '../../../../../../components/Filters/SidebarListItem';
import SidebarFooter from '../../../../../../components/Filters/SidebarFooter';
import { defaultScriptExecutionDevicesFilters } from '../../../../../../components/Filters/constants';
import { sidebarList } from './constants';
import { pagesText } from '../../../../../../constants/pagesText';

const FilterSidebarTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontWeight: 500,
  fontSize: theme.typography.pxToRem(18),
  lineHeight: '24px',
  marginLeft: theme.spacing(3.5),
}));

const SidebarDivider = () => (
  <Divider
    orientation="horizontal"
    sx={{
      backgroundColor: '#2C2C2C',
    }}
  />
);

const WdacScriptExecutionSidebar = ({
  open,
  onClose,
  onApplyFilters,
  initialQueryFilters,
  query,
}) => {
  const [activeFilterSection, setActiveFilterSection] = useState(null);

  const initialFilters = useMemo(
    () => ({
      FilterScriptExecutionAffectedDevices:
        initialQueryFilters?.FilterScriptExecutionAffectedDevices ||
        defaultScriptExecutionDevicesFilters.FilterScriptExecutionAffectedDevices,
    }),
    [initialQueryFilters],
  );

  const [selectedFilters, setSelectedFilters] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);

  const onChangeActiveSidebar = useCallback(
    activeSection => {
      setActiveFilterSection(activeSection);
    },
    [setActiveFilterSection],
  );

  const onDrawerClose = useCallback(() => {
    onClose();
    setActiveFilterSection(null);
    setSelectedFilters(filters);
  }, [onClose, setActiveFilterSection, setSelectedFilters, filters]);

  const onChangeFilterValue = useCallback(
    ({ source, fieldIndex, sectionIndex, fieldValue, label }) => {
      setSelectedFilters(prevState => {
        const newSourceData = [...prevState[source]];
        newSourceData[sectionIndex] = newSourceData[sectionIndex].map((filterField, index) => {
          if (index === fieldIndex) {
            return {
              ...filterField,
              value: fieldValue,
            };
          }
          return filterField;
        });

        return {
          ...prevState,
          [source]: newSourceData,
        };
      });
    },
    [],
  );

  const onAddFilter = useCallback(() => {
    const selectedFilterField = activeFilterSection.name;
    setSelectedFilters(prevState => ({
      ...prevState,
      [selectedFilterField]: [
        ...prevState[selectedFilterField],
        ...defaultScriptExecutionDevicesFilters[selectedFilterField],
      ],
    }));
  }, [activeFilterSection?.name, setSelectedFilters]);

  const onRemoveFilter = useCallback(
    sectionIndex => {
      const selectedFilterField = activeFilterSection.name;
      setSelectedFilters(prevState => ({
        ...prevState,
        [selectedFilterField]: prevState[selectedFilterField].filter(
          (filter, filterIndex) => filterIndex !== sectionIndex,
        ),
      }));
    },
    [activeFilterSection?.name, setSelectedFilters],
  );

  const resetFilters = useCallback(() => {
    if (!activeFilterSection) {
      setFilters(defaultScriptExecutionDevicesFilters);
      setSelectedFilters(defaultScriptExecutionDevicesFilters);
      return;
    }

    setFilters(prevState => ({
      ...prevState,
      [activeFilterSection.name]: defaultScriptExecutionDevicesFilters[activeFilterSection.name],
    }));
    setSelectedFilters(prevState => ({
      ...prevState,
      [activeFilterSection.name]: defaultScriptExecutionDevicesFilters[activeFilterSection.name],
    }));
    setActiveFilterSection(null);
  }, [activeFilterSection]);

  const onSuccess = useCallback(() => {
    if (!!activeFilterSection) {
      setFilters(prevState => ({
        ...prevState,
        [activeFilterSection.name]: selectedFilters[activeFilterSection.name],
      }));
      setActiveFilterSection(null);
      return;
    }

    onApplyFilters(selectedFilters, 'apply');
    onDrawerClose();
  }, [activeFilterSection, onApplyFilters, selectedFilters, onDrawerClose]);

  const onBackClick = useCallback(() => {
    setActiveFilterSection(null);
  }, [setActiveFilterSection]);

  const isApplyFiltersDisabled = useMemo(() => {
    if (!activeFilterSection) return false;

    const activeFilterSectionFilters = selectedFilters[activeFilterSection.name];
    if (!activeFilterSectionFilters) return true;

    return activeFilterSectionFilters.flat().every(filter => filter.value === '');
  }, [activeFilterSection, selectedFilters]);

  const checkIsFilterListItemActive = useCallback(
    name => filters[name].flat().some(item => item.value !== ''),
    [filters],
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onDrawerClose}
      PaperProps={{
        sx: theme => ({
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(6, 0),
          width: '100%',
          maxWidth: '344px',
          backgroundImage: 'unset',
        }),
      }}>
      <Box position="relative" height="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 1.5,
          }}>
          <FilterSidebarTitle>Filter By Column</FilterSidebarTitle>
          <DeleteButton
            onClick={resetFilters}
            hasIcon={false}
            styles={{
              marginRight: '28px',
            }}>
            {pagesText.filters.resetFiltersButton}
          </DeleteButton>
        </Stack>
        <SidebarDivider />
        <Scrollbar
          height="calc(100% - 80px)"
          styles={{
            padding: '0 22px',
          }}>
          {activeFilterSection ? (
            <ActiveFilterSection
              title={activeFilterSection.title}
              source={activeFilterSection.name}
              filters={selectedFilters[activeFilterSection.name]}
              onChangeFilterValue={onChangeFilterValue}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onBackClick={onBackClick}
            />
          ) : (
            <Box display="flex" mt={2.5} flexDirection="column" gap={2.25}>
              {sidebarList.map(({ title, name, options = [] }) => (
                <SidebarListItem
                  name={name}
                  key={name}
                  title={title}
                  options={options}
                  query={query}
                  isActive={checkIsFilterListItemActive(name)}
                  onChangeFilterValue={onChangeFilterValue}
                  onClick={() => onChangeActiveSidebar({ title, name })}
                />
              ))}
            </Box>
          )}
        </Scrollbar>
        <SidebarFooter
          onClose={onDrawerClose}
          onSuccess={onSuccess}
          isApplyFiltersDisabled={isApplyFiltersDisabled}
          isActiveFilterSection={!!activeFilterSection?.name}
        />
      </Box>
    </Drawer>
  );
};

export default memo(WdacScriptExecutionSidebar);
